<script setup lang="ts">

import AddressVerificationDocument from "@/views/pages/profile/AddressVerificationDocument.vue";
import {computed, onMounted, ref} from "vue";
import HorizontalFormGroup from "@/components/forms/horizontal/HorizontalFormGroup.vue";

const props = defineProps({
  maxDocuments: {
    type: Number,
    default: 3
  },
  disabled: {
    type: Boolean,
    default: false
  },
  model: {
    type: Object,
    default: null
  },
  modelAttributePrefix: {
    type: String,
    default: 'addressDocument'
  }
})

const documentsCount = ref(1);
const documents = ref([]);

onMounted(() => {
  refreshDocumentsList()
})

const uuidv4 = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = Math.random() * 16 | 0,
      v = c === 'x'
        ? r
        : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

const documentRefs = ref([])

const refreshDocumentsList = () => {
  const visibleCount = documents.value.filter((document) => document.visible).length;
  const countToAdd = documentsCount.value - visibleCount;
  for (let i = 0; i < countToAdd; i++) {
    documents.value.push({
      visible: true,
      id: uuidv4(),
    });
  }

  onDocumentChanged();
}

const visibleDocuments = computed(() => {
  return documents.value.filter((document) => document.visible);
})

const addDocument = () => {
  if (documentsCount.value < props.maxDocuments) {
    documentsCount.value++;
    refreshDocumentsList();
  }
}

const setItemRef = (el) => {
  if (el) {
    documentRefs.value.push(el)
  }
}

const removeDocument = (id) => {

  // console.log('refs', documentRefs);
  // documents[index].visible = false;
  // console.log('remove ', documents.value)
  const idx = documents.value.findIndex((document) => document.id === id);
  documents.value[idx].visible = false;
  documentRefs.value[idx].resetDocument();
  if (documentsCount.value > 1)
    documentsCount.value--;
  refreshDocumentsList();
}

const getDocuments = () => {
  let result = [];
  documents.value.forEach((document, index) => {
    if (document.visible && documentRefs.value[index]?.documentInfo)
    {
      result = [...result, ...documentRefs.value[index].documentInfo];
    }
  })
  return result;
}

const visibleDocumentRefs = computed(() => {
  const result = [];
  documents.value.forEach((document, index) => {
    if (document.visible && documentRefs.value[index])
    {
      result.push(documentRefs.value[index]);
    }
  })
  return result;
})

const busy = computed(() => {
  return visibleDocumentRefs.value.some((document) => document.busy);
})

const reset = () => {
  documentsCount.value = 1;
  documents.value.forEach((document, index) => {
    document.visible = false;
    documentRefs.value[index].resetDocument();
  })
  refreshDocumentsList();
}

const emits = defineEmits(['input']);

const onDocumentChanged = () => {
  // console.log('document changed', value);
  const docs = getDocuments();
  // console.log('documents total', docs);
  emits('input', docs);
}

defineExpose({getDocuments, reset, busy})
</script>

<template>
  <div>
    <div class="documents--list">
      <div v-for="(document, index) in visibleDocuments" :key="document.id">
        <HorizontalFormGroup>
          <div class="document--heading">
            <h4>{{ $t('Документ #') + (Number(index)+1) }}</h4>
            <button type="button"
                    :disabled="disabled"
                    @click="removeDocument(document.id)"
                    class="btn btn-success-outline btn-sm btn-small">{{ $t('Удалить документ') }}
            </button>
          </div>
        </HorizontalFormGroup>
        <AddressVerificationDocument
          :disabled="disabled"
          :model="props.model"
          :modelAttributesPrefix="props.modelAttributePrefix + '[' + index + '].'"
          @input="onDocumentChanged"
          :ref="setItemRef"></AddressVerificationDocument>
      </div>
    </div>
    <template v-if="documentsCount < props.maxDocuments">
      <HorizontalFormGroup style="margin-top:50px;">
        <button @click="addDocument" type="button" :disabled="disabled" class="btn btn-success">
          {{ $t('Добавить документ') }}
        </button>
      </HorizontalFormGroup>
    </template>
  </div>
</template>

<style scoped lang="less">
.documents--list {
  display: flex;
  flex-direction: column;
  gap: 50px;
}

.document--heading {
  display: flex;
  align-items: center;
  gap: 30px;
  justify-content: flex-start;
  flex-direction: row;

  h4 {
    font-weight: 900;
  }
}
</style>

<i18n>
{
  "en": {
    "Добавить документ": "Add document",
    "Удалить документ": "Remove document",
    "Документ #": "Document #",
    "Тип документа": "Document type"
  }
}
</i18n>
